import { useEffect, useRef, useState } from 'react';

import { Box, Divider, Flex } from '@chakra-ui/react';
import { Button, Fade, Menu, MenuItem } from '@material-ui/core';
import { useQueryClient } from '@tanstack/react-query';
import { closeCreateKRPanelAction, openCreateKRPanelAction } from 'actions/createKRPanelActions';
import {
  closeObjectiveDetailsPanelActionCreator,
  openObjectiveDetailsPanelActionCreator,
} from 'actions/objectiveDetailsPanelActions';
import { closeObjectivePromptModal, openObjectivePromptModal } from 'actions/objectivePromptModalActions';
import {
  CloneObjectiveModalView,
  DeleteKRModalGoalsRevamp,
  DeleteObjectiveModalGoalsRevamp,
} from 'components/ObjectiveTagList/ModalPromptView';
import { useOKRAccessControl } from 'components/RabbitOKRSidePanel/AccessControl/useOKRAccessControl';
import { showOKRDetails } from 'components/RabbitOKRSidePanel/okrPanelControlSlice';
import {
  performClone,
  performDelete,
  performRealign,
} from 'components/RabbitOKRSidePanel/okrPanelKebabMenuActionsSlice';
import { openRealignModalAction } from 'components/RealignModal/redux/actions';
import { useViewPageName } from 'components/Views/Hooks/useViewPageName';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { openCreateOKRPanel } from 'reducers/createObjectivePanelSlice';
import { closeKRPromptModal, openKRPromptModal } from 'reducers/keyResultPromptModal';
import { showErrorMessage } from 'screens/performanceReview/components/common';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';
import { okrsPageSelector } from 'selectors/okrsPage';
import { selectOrgData } from 'selectors/orgData';
import { selectLatestObjectivePanelDetails } from 'selectors/selectObjectivePanelDetails';
import styled from 'styled-components';
import { checkUserIsGoalAdmin } from 'utils/helpers/rolesAccess';

import {
  Clone,
  Delete,
  KeyResultIcon,
  ObjectiveIcon,
  OpenSidePanel,
  Realign,
  Archive,
  ThreeDots,
} from 'assets/assetsV1/iconComponents';

import { OBJECTIVE_ACCESS_PERMISSIONS, OKR_TYPES } from 'constants/ProjectConstants';
import {
  sendClickedOnCreateKRevent,
  sendClickedOnCreateObjectiveEvent,
  sendCreatedKREvent,
  sendCreatedObjectiveEvent,
  sendOpenedObjectivePanelEvent,
  sendOpenedKRPanelEvent,
  sendPageViewEvent,
  sendClickedOnKebabMenuEvent,
  sendClickedRealignedEvent,
} from 'constants/mixpanelHelper';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';

import { getObjective } from 'services/objective';
import { showErrorSnackbar, showSuccessSnackbar } from 'services/snackbar';
import { deleteTask } from 'services/task';
import { showTaskDetails } from 'services/taskDetails';

import { SkeletonLoader } from 'uiToolkitV1/SkeletonLoader';
import { Typography } from 'uiToolkitV1/Typography/Typography';

const KebabTableMenu = (props) => {
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const page = useViewPageName();

  const { selectedTimelines, allTimelines, timelinesData } = useSelector(okrsPageSelector);
  const loggedInEmployee = useSelector(selectLoggedInEmployeeData, shallowEqual);
  const { moduleName, objectivesName, keyResultsName } = useOrganisationSettings();

  const { objectiveCreationAccess } = useSelector(selectOrgData);
  const queryClient = useQueryClient();

  const { onReAlign, onDelete } = useSelector(selectLatestObjectivePanelDetails) ?? {};

  const [currentObjectiveDetails, setCurrentObjectiveDetails] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const textAreaRef = useRef();
  const openMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const fetchObjectiveDetails = async (id) => {
    try {
      const res = await getObjective({ objectivePId: id });
      setCurrentObjectiveDetails(res);
    } catch (error) {}
  };

  useEffect(() => {
    if (anchorEl) {
      fetchObjectiveDetails(props.data.uuid);
    }
  }, [anchorEl, props.data.uuid]);

  const closeMenu = () => {
    setAnchorEl(null);
    setCurrentObjectiveDetails(null);
  };

  const refetchHandler = () => {
    queryClient.invalidateQueries([REACT_QUERY_KEYS.GoalsCascadedData]);
  };

  const openDetailsPanel = () => {
    const { type, uuid, unMasked } = props.data ?? {};
    if (!unMasked) {
      return;
    }
    if (type === 'OBJECTIVE') {
      // navigate to objective sidepanel
      sendClickedOnCreateObjectiveEvent({
        eventProperties: { source: 'Kebab Menu', page },
      });
      sendOpenedObjectivePanelEvent({
        eventProperties: { source: 'kebab Menu', page, type: 'Edit Obj Panel' },
      });
      dispatch(
        openObjectiveDetailsPanelActionCreator({
          objectivePId: uuid,
          onNameChange: (objectiveName) => {
            refetchHandler();
          },
          onDelete: () => {
            refetchHandler();
            dispatch(showSuccessSnackbar('Objective deleted!'));
          },
          onLevelChange: () => {
            refetchHandler();
          },
          onReAlign: () => {
            refetchHandler();
            dispatch(showSuccessSnackbar('Objective realigned!'));
          },
        })
      );
    }
    if (type === 'GOAL') {
      // navigate to goal sidepanel
      sendClickedOnCreateKRevent({
        eventProperties: { source: 'Kebab Menu', page },
      });
      sendOpenedKRPanelEvent({
        eventProperties: { source: 'kebab Menu', page, type: 'Edit KR Panel' },
      });
      dispatch(
        showTaskDetails({
          taskIds: [uuid],
          onDelete: () => {
            refetchHandler();
          },
          onUpdate: () => {
            refetchHandler();
          },
          employee: {},
          type,
          history,
        })
      );
    }
    closeMenu();
  };

  const openKRCreationPanel = () => {
    const closeCreateKRPanel = () => dispatch(closeCreateKRPanelAction());
    closeMenu();
    dispatch(
      openCreateKRPanelAction({
        onClose: closeCreateKRPanel,
        onCancel: closeCreateKRPanel,
        // onSave: onKeyResultCreation,
        selectedTimeBoundPeriod: selectedTimelines,
        ownerEmployee: loggedInEmployee,
        subObjective: {
          ...currentObjectiveDetails?.objectiveTimeBound,
          postCardId: currentObjectiveDetails?.id,
        },
        // actionDispatchSource: isTeamHubView ? 'Team Page' : 'Insights',
        panelHeaderText: `Add ${keyResultsName}`,
      })
    );
  };

  const openObjectiveCreationPanel = () => {
    closeMenu();
    dispatch(
      openCreateOKRPanel({
        selectedObjective: {
          ...currentObjectiveDetails?.objectiveTimeBound,
          postCardId: currentObjectiveDetails?.id,
        },
        selectedTimeBoundPeriod: selectedTimelines,
        isAlignmentAtParentLevel: true,
        history,
        location,
      })
    );
  };

  const openRealignPanel = () => {
    sendClickedRealignedEvent({
      eventProperties: { source: 'kebab Menu', page, type: 'Realign', goalId: props?.data?.uuid },
    });
    closeMenu();
    dispatch(
      openRealignModalAction({
        objectiveId: currentObjectiveDetails?.objectiveTimeBound?.objectiveId,
        realignId: currentObjectiveDetails?.objectiveTimeBound?.objectiveTimeBoundId,
        selectedTimeBoundPeriod: currentObjectiveDetails?.objectiveTimeBound?.timeBoundDetails,
        isRealignObjective: true,
        objectiveName: currentObjectiveDetails?.objectiveTimeBound?.name || '-',
        selectedObjective: {
          timeBoundDetails: currentObjectiveDetails?.objectiveTimeBound?.timeBoundDetails,
          objectiveId: currentObjectiveDetails?.objectiveTimeBound?.objectiveId,
        },
        onSuccess: () => {
          refetchHandler();
          if (typeof onReAlign === 'function') {
            onReAlign();
          }
        },
      })
    );
  };

  const handleDelete = () => {
    sendClickedRealignedEvent({
      eventProperties: { source: 'kebab Menu', page, type: 'Delete', goalId: props?.data?.uuid },
    });
    closeMenu();
    if (props.data.type === 'OBJECTIVE') {
      const promptHandler = (deletionSuccess) => {
        dispatch(closeObjectivePromptModal());
        if (deletionSuccess) {
          refetchHandler();
          dispatch(closeObjectiveDetailsPanelActionCreator({ pushToHistory: true }));

          if (typeof onDelete === 'function') onDelete();
        }
      };

      dispatch(
        openObjectivePromptModal({
          contentComponent: (
            <DeleteObjectiveModalGoalsRevamp
              onCompletion={promptHandler}
              objectiveTimeBoundId={currentObjectiveDetails?.objectiveTimeBound?.objectiveTimeBoundId}
              tag={currentObjectiveDetails?.objectiveTimeBound}
            />
          ),
        })
      );
    }
    if (props.data.type === 'GOAL') {
      dispatch(
        openKRPromptModal(
          <DeleteKRModalGoalsRevamp
            onDelete={() => {
              deleteTask({ id: props.data.uuid })
                .then((res) => {
                  refetchHandler();
                  queryClient.invalidateQueries([REACT_QUERY_KEYS.GoalsCascadedData]);
                  dispatch(closeKRPromptModal());
                })
                .catch((err) => {
                  dispatch(showErrorSnackbar('There is some problem. Please try again!'));
                });
            }}
          />
        )
      );
    }
  };

  const shareObjective = (text) => {
    closeMenu();
    textAreaRef.current.select();
    navigator.clipboard
      .writeText(text)
      .then(() => {
        dispatch(showSuccessSnackbar('Successfully copied url to clipboard'));
      })
      .catch(() => {
        dispatch(showErrorMessage('Error while copying url to clipboard'));
      });
  };

  const cloneObjective = () => {
    closeMenu();
    const closeCloseObjectiveModal = () => {
      dispatch(closeObjectivePromptModal());
    };
    dispatch(
      openObjectivePromptModal({
        contentComponent: (
          <CloneObjectiveModalView
            onCompletion={closeCloseObjectiveModal}
            sourceObjective={currentObjectiveDetails?.objectiveTimeBound}
            timePeriodData={timelinesData}
            timePeriodsList={allTimelines}
          />
        ),
      })
    );
  };

  const showObjectiveOption = () => {
    if (objectiveCreationAccess === OBJECTIVE_ACCESS_PERMISSIONS[0].value) {
      return checkUserIsGoalAdmin();
    }
    if (objectiveCreationAccess === OBJECTIVE_ACCESS_PERMISSIONS[1].value) {
      return checkUserIsGoalAdmin() || loggedInEmployee?.isHRISManager;
    }

    return false;
  };

  return (
    <Flex height="100%" alignItems="center" justifyContent="center">
      <Button
        aria-controls="simple-menu"
        aria-haspopup="true"
        disableRipple="true"
        onClick={(event) => {
          sendClickedOnKebabMenuEvent({
            eventProperties: {
              source: props.source || 'Goals Homepage',
              type: page || 'Home',
              cohort: page || 'Home',
            },
          });
          openMenu(event);
        }}
        style={{ backgroundColor: anchorEl ? '#e6f1fd' : null, minWidth: 'unset' }}
      >
        <ThreeDots sx={{ transform: 'rotate(90deg)' }} boxSize="24px" />
      </Button>
      <StyledMenu
        id="simple-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={closeMenu}
        elevation={0}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        style={{
          marginTop: '1rem',
          boxShadow: '2px 0px 4px 3px rgba(0, 0, 0, 0.12), 0px 4px 4px 3px rgba(0, 0, 0, 0.08)',
        }}
        TransitionComponent={Fade}
      >
        {!currentObjectiveDetails ? (
          // <StyledMenuItem style={{ width: '100px' }}>
          <StyledMenuItem disableRipple style={{ cursor: 'default', background: 'transparent' }}>
            <Box pl={2} width="100px" height="120px">
              <SkeletonLoader />
            </Box>
          </StyledMenuItem>
        ) : (
          <>
            <StyledMenuItem
              onClick={() => {
                openDetailsPanel();
                sendPageViewEvent({
                  eventProperties: {
                    page: props?.data?.type === 'OBJECTIVE' ? 'Add Objective Panel' : 'Add KR Panel',
                    type: 'Panel',
                    source: 'kebab Menu',
                  },
                });
              }}
            >
              <OpenSidePanel boxSize="20px" />

              <Typography ml={2}>View Details</Typography>
            </StyledMenuItem>
            {props.data.type === 'OBJECTIVE' && (
              <>
                <Divider />
                {showObjectiveOption() && (
                  <StyledMenuItem onClick={openObjectiveCreationPanel}>
                    <ObjectiveIcon boxSize="20px" />
                    <Typography ml={2}>New {objectivesName}</Typography>
                  </StyledMenuItem>
                )}
                <StyledMenuItem onClick={openKRCreationPanel}>
                  <KeyResultIcon boxSize="20px" />
                  <Typography ml={2}>New {keyResultsName}</Typography>
                </StyledMenuItem>
                <Divider />
                <StyledMenuItem onClick={openRealignPanel}>
                  <Realign boxSize="20px" />
                  <Typography ml={2}>Re-align</Typography>
                </StyledMenuItem>
                {/* <StyledMenuItem onClick={shareObjective}>
              <Share boxSize="20px" />
              <Typography ml={2}>Share</Typography>
            </StyledMenuItem> */}
                <StyledMenuItem onClick={cloneObjective}>
                  <Clone boxSize="20px" />
                  <Typography ml={2}>Make a Copy</Typography>
                </StyledMenuItem>
                <StyledMenuItem onClick={handleDelete}>
                  <Delete boxSize="20px" />
                  <Typography ml={2}>Delete</Typography>
                </StyledMenuItem>
              </>
            )}

            {props.data.type === 'GOAL' && (
              <>
                <Divider />
                <StyledMenuItem onClick={handleDelete}>
                  <Delete boxSize="20px" />
                  <Typography ml={2}>Delete</Typography>
                </StyledMenuItem>
              </>
            )}
          </>
        )}
      </StyledMenu>
    </Flex>
  );
};

export const KebabTableMenuPanelsRevamp = (props) => {
  const history = useHistory();
  const location = useLocation();
  const okr = props.data;
  const dispatch = useDispatch();

  const { selectedTimelines } = useSelector(okrsPageSelector);

  const timeBoundDetails = okr?.timeBoundDetails ?? selectedTimelines?.[0];

  const { moduleName, objectivesName, keyResultsName } = useOrganisationSettings();
  const queryClient = useQueryClient();
  const page = useViewPageName();

  const [anchorEl, setAnchorEl] = useState(null);
  const openMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const closeMenu = () => {
    setAnchorEl(null);
  };

  const refetchHandler = () => {
    queryClient.invalidateQueries([REACT_QUERY_KEYS.GoalsCascadedData]);
    queryClient.invalidateQueries([REACT_QUERY_KEYS.GoalsInsightsGroupByGoalsData]);
  };

  const openDetailsPanel = () => {
    const { type, uuid, unMasked } = props.data ?? {};
    if (!unMasked) {
      return;
    }

    if (type === 'OBJECTIVE') {
      // navigate to objective sidepanel
      sendClickedOnCreateObjectiveEvent({
        eventProperties: { source: 'Kebab Menu', page },
      });
      sendOpenedObjectivePanelEvent({
        eventProperties: { source: 'kebab Menu', page, type: 'Edit Obj Panel' },
      });
    } else {
      sendClickedOnCreateKRevent({
        eventProperties: { source: 'Kebab Menu', page },
      });
      sendOpenedKRPanelEvent({
        eventProperties: { source: 'kebab Menu', page, type: 'Edit KR Panel' },
      });
    }

    sendPageViewEvent({
      eventProperties: {
        page: props?.data?.type === 'OBJECTIVE' ? 'Add Objective Panel' : 'Add KR Panel',
        type: 'Panel',
        source: 'kebab Menu',
      },
    });

    dispatch(
      showOKRDetails({
        id: uuid,
        type,
        onDelete: refetchHandler,
        onUpdate: refetchHandler,
        history,
      })
    );
    closeMenu();
  };

  const openObjectiveCreationPanel = ({ okrPanelType = OKR_TYPES.OBJECTIVE }) => {
    closeMenu();
    dispatch(
      openCreateOKRPanel({
        selectedObjective: {
          uuid: props?.data?.uuid,
          text: props?.data?.title,
          type: props?.data?.type === OKR_TYPES.OBJECTIVE ? OKR_TYPES.OBJECTIVE : OKR_TYPES.KEY_RESULT,
          visibilityType: props?.data?.visibilityType,
        },
        selectedTimeBoundPeriod: timeBoundDetails,
        okrPanelType,
        parentMeasure: props?.data?.metric,
        parentMeasureType: props?.data?.metricName,
        parentProgressType: props?.data?.progressType,
        parentPost: okr?.parentPost,
        onKeyResultCreation(okrDetails) {
          sendCreatedKREvent({
            eventProperties: {
              source: 'Kebab Menu',
              page,
              goalId: okrDetails?.uuid,
            },
          });
        },
        onObjectiveCreation(okrDetails) {
          sendCreatedObjectiveEvent({
            eventProperties: {
              source: 'Kebab Menu',
              page,
              objectiveId: okrDetails?.uuid,
              description: okrDetails?.description,
              level: okrDetails?.objectiveLevel,
              type: okrDetails?.type,
              timePeriod: okrDetails?.timeBoundDetails?.timelineTag,
            },
          });
        },
        history,
        location,
      })
    );
  };

  const openKRCreationPanel = () => {
    openObjectiveCreationPanel({ okrPanelType: OKR_TYPES.KEY_RESULT });
  };

  const openRealignPanel = () => {
    sendClickedRealignedEvent({
      eventProperties: { source: 'kebab Menu', page, type: 'Realign', goalId: props?.data?.uuid },
    });
    openDetailsPanel();
    dispatch(performRealign());
  };

  const handleDelete = () => {
    sendClickedRealignedEvent({
      eventProperties: { source: 'kebab Menu', page, type: 'Delete', goalId: props?.data?.uuid },
    });
    openDetailsPanel();
    dispatch(performDelete());
  };

  const cloneObjective = () => {
    openDetailsPanel();
    dispatch(performClone());
  };

  /* Access Control for OKR Menu Options starts here */
  const { canDeleteOKR, canEditOKR, canCreateKeyResult, canCreateObjective, canCloneKeyResult, canCloneObjective } =
    useOKRAccessControl(okr);

  /* Access Control for OKR Menu Options starts here */

  let optionsToShow = ['CREATE_OBJECTIVE', 'CREATE_KR', 'REALIGN', 'CLONE', 'DELETE'];

  if (!canDeleteOKR) {
    optionsToShow = optionsToShow.filter((item) => item !== 'DELETE');
  }
  if (!canEditOKR) {
    optionsToShow = optionsToShow.filter((item) => item !== 'REALIGN');
  }

  if (!canCreateKeyResult) {
    optionsToShow = optionsToShow.filter((item) => item !== 'CREATE_KR');
  }
  if (!canCreateObjective) {
    optionsToShow = optionsToShow.filter((item) => item !== 'CREATE_OBJECTIVE');
  }

  if (okr?.type === OKR_TYPES.OBJECTIVE) {
    optionsToShow = canCloneObjective ? optionsToShow : optionsToShow.filter((item) => item !== 'CLONE');
  } else {
    optionsToShow = canCloneKeyResult ? optionsToShow : optionsToShow.filter((item) => item !== 'CLONE');
  }

  return (
    <Flex height="100%" alignItems="center" justifyContent="center">
      <Button
        aria-controls="simple-menu"
        aria-haspopup="true"
        disableRipple="true"
        onClick={(event) => {
          sendClickedOnKebabMenuEvent({
            eventProperties: {
              source: props.source || 'Goals Homepage',
              type: page || 'Home',
              cohort: page || 'Home',
            },
          });
          openMenu(event);
        }}
        style={{ backgroundColor: anchorEl ? '#e6f1fd' : null, minWidth: 'unset' }}
      >
        <ThreeDots sx={{ transform: 'rotate(90deg)' }} boxSize="24px" />
      </Button>

      <StyledMenu
        id="simple-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={closeMenu}
        elevation={0}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        style={{
          marginTop: '1rem',
          boxShadow: '2px 0px 4px 3px rgba(0, 0, 0, 0.12), 0px 4px 4px 3px rgba(0, 0, 0, 0.08)',
        }}
        TransitionComponent={Fade}
      >
        <>
          <StyledMenuItem onClick={openDetailsPanel}>
            <OpenSidePanel boxSize="20px" />

            <Typography ml={2}>View Details</Typography>
          </StyledMenuItem>
          {optionsToShow.includes('CREATE_OBJECTIVE') || optionsToShow.includes('CREATE_KR') ? (
            <>
              <Divider />
              {optionsToShow.includes('CREATE_OBJECTIVE') && (
                <StyledMenuItem
                  onClick={() => {
                    openObjectiveCreationPanel({ okrPanelType: OKR_TYPES.OBJECTIVE });
                    sendClickedOnCreateObjectiveEvent({
                      eventProperties: { source: 'Kebab Menu', page },
                    });
                    sendOpenedObjectivePanelEvent({
                      eventProperties: { source: 'kebab Menu', page, type: 'Add Objective Panel' },
                    });
                    sendPageViewEvent({
                      eventProperties: {
                        page: 'Add Objective Panel',
                        type: 'Panel',
                        source: 'kebab Menu',
                      },
                    });
                  }}
                >
                  <ObjectiveIcon boxSize="20px" />
                  <Typography ml={2}>Add Child {objectivesName}</Typography>
                </StyledMenuItem>
              )}
              {optionsToShow.includes('CREATE_KR') && (
                <StyledMenuItem
                  onClick={() => {
                    openKRCreationPanel();
                    sendClickedOnCreateKRevent({
                      eventProperties: { source: 'Kebab Menu', page },
                    });
                    sendOpenedKRPanelEvent({
                      eventProperties: { source: 'kebab Menu', page, type: 'Add KR Panel' },
                    });
                    sendPageViewEvent({
                      eventProperties: {
                        page: 'Add KR Panel',
                        type: 'Panel',
                        source: 'kebab Menu',
                      },
                    });
                  }}
                >
                  <KeyResultIcon boxSize="20px" />
                  <Typography ml={2}>Add Child {keyResultsName}</Typography>
                </StyledMenuItem>
              )}

              <Divider />
            </>
          ) : null}
          {optionsToShow.includes('REALIGN') ? (
            <StyledMenuItem onClick={openRealignPanel}>
              <Realign boxSize="20px" />
              <Typography ml={2}>Re-align</Typography>
            </StyledMenuItem>
          ) : null}

          {optionsToShow.includes('CLONE') ? (
            <StyledMenuItem onClick={cloneObjective}>
              <Clone boxSize="20px" />
              <Typography ml={2}>Make a Clone</Typography>
            </StyledMenuItem>
          ) : null}

          {optionsToShow.includes('DELETE') ? (
            <StyledMenuItem onClick={handleDelete}>
              <Archive boxSize="20px" />
              <Typography ml={2}>Archive</Typography>
            </StyledMenuItem>
          ) : null}
        </>
      </StyledMenu>
    </Flex>
  );
};

export default KebabTableMenu;

const StyledMenuItem = styled(MenuItem)`
  padding: 8px 16px 8px 6px;
  border-radius: 4px;

  font-family: inherit;

  &:hover {
    color: #104f95;
    background-color: #e6f1fd;

    svg {
      color: #104f95;
    }
  }
`;

const StyledMenu = styled(Menu)`
  .MuiMenu-paper {
    transition: 0.5s ease;
    box-shadow:
      2px 0px 4px 3px rgba(0, 0, 0, 0.12),
      0px 4px 4px 3px rgba(0, 0, 0, 0.08) !important;
  }
  ul {
    padding: 8px;
  }
`;
